import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
 
/* ─── Design tokens ─────────────────────────────────────────── */
const T = {
  green: "#1e6b3a",
  greenMid: "#2d8c50",
  greenLight: "#e8f5ee",
  greenGlow: "#d0edda",
  greenDark: "#134527",
  accent: "#f5a623",
  accentSoft: "#fef6e8",
  bg: "#f4f6f3",
  surface: "#ffffff",
  border: "#dde3d8",
  borderFocus: "#2d8c50",
  text: "#1a2318",
  textMid: "#4a5c45",
  textMuted: "#7a8c76",
  red: "#c0392b",
  redSoft: "#fdf0ee",
  shadow: "0 1px 3px rgba(30,107,58,0.08), 0 4px 16px rgba(30,107,58,0.06)",
  shadowCard:
    "0 2px 8px rgba(30,107,58,0.07), 0 12px 32px rgba(30,107,58,0.05)",
};
 
const STEPS = [
  { id: 1, label: "Personal", icon: "👤", desc: "Basic details" },
  { id: 2, label: "Location", icon: "📍", desc: "Address info" },
  { id: 3, label: "Eligibility", icon: "✅", desc: "Readiness" },
];
 
const STEP_FIELDS = {
  1: [
    "fullName",
    "phoneNumber",
    "aadhaarNumber",
    "educationalQualification",
    "ageGroup",
  ],
  2: ["state", "district", "tehsil", "block", "address"],
  3: [
    "trainingAvailability",
    "documentStatus",
    "loanOption",
    "readyToProceed",
    "bookingAmountStatus",
  ],
};
 
const initialForm = {
  fullName: "",
  phoneNumber: "",
  email: "",
  state: "",
  district: "",
  tehsil: "",
  block: "",
  aadhaarNumber: "",
  educationalQualification: "",
  address: "",
  ageGroup: "",
  trainingAvailability: "",
  documentStatus: "",
  loanOption: "",
  readyToProceed: "",
  bookingAmountStatus: "",
};
 
/* ─── Inject global styles ──────────────────────────────────── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Serif+Display:ital@0;1&display=swap%27);
 
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
 
  .dsk-page {
    min-height: 100vh;
    background: ${T.bg};
    padding: 36px 16px 60px;
    font-family: 'DM Sans', sans-serif;
    background-image:
      radial-gradient(ellipse 80% 40% at 50% -10%, rgba(30,107,58,0.09) 0%, transparent 70%);
  }
 
  .dsk-shell { max-width: 660px; margin: 0 auto; }
 
  /* ── Brand header ── */
  .dsk-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 28px;
  }
  .dsk-brand { display: flex; align-items: center; gap: 12px; }
  .dsk-logo {
    width: 42px; height: 42px; background: ${T.green}; border-radius: 12px;
    display: flex; align-items: center; justify-content: center; font-size: 20px;
    box-shadow: 0 4px 12px rgba(30,107,58,0.3);
  }
  .dsk-brand-name {
    font-family: 'DM Serif Display', serif; font-size: 18px;
    color: ${T.greenDark}; line-height: 1.2;
  }
  .dsk-brand-sub { font-size: 11.5px; color: ${T.textMuted}; letter-spacing: 0.5px; margin-top: 1px; }
  .dsk-badge {
    font-size: 11px; color: ${T.green}; background: ${T.greenLight};
    border: 1px solid ${T.greenGlow}; border-radius: 20px;
    padding: 4px 12px; font-weight: 500; letter-spacing: 0.3px;
  }
 
  /* ── Progress track ── */
  .dsk-progress-track {
    height: 4px; background: ${T.border}; border-radius: 4px;
    margin-bottom: 28px; overflow: hidden;
  }
  .dsk-progress-fill {
    height: 100%; border-radius: 4px;
    background: linear-gradient(90deg, ${T.greenMid}, ${T.accent});
    transition: width 0.5s cubic-bezier(.4,0,.2,1);
  }
 
  /* ── Stepper ── */
  .dsk-stepper {
    display: flex; align-items: center; margin-bottom: 28px;
    background: ${T.surface}; border: 1px solid ${T.border};
    border-radius: 14px; padding: 12px 20px;
    box-shadow: ${T.shadow};
  }
  .dsk-step { display: flex; align-items: center; gap: 10px; flex: 1; }
  .dsk-step-circle {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 600; transition: all 0.3s ease;
    border: 2px solid transparent;
  }
  .dsk-step-circle.done {
    background: ${T.greenLight}; border-color: ${T.greenMid}; color: ${T.green}; font-size: 15px;
  }
  .dsk-step-circle.active {
    background: ${T.green}; color: white;
    box-shadow: 0 0 0 4px ${T.greenGlow};
  }
  .dsk-step-circle.idle {
    background: ${T.bg}; border-color: ${T.border}; color: ${T.textMuted};
  }
  .dsk-step-text { display: flex; flex-direction: column; gap: 1px; }
  .dsk-step-label { font-size: 12.5px; font-weight: 600; }
  .dsk-step-label.active { color: ${T.greenDark}; }
  .dsk-step-label.done   { color: ${T.greenMid}; }
  .dsk-step-label.idle   { color: ${T.textMuted}; }
  .dsk-step-desc { font-size: 11px; color: ${T.textMuted}; }
  .dsk-step-divider { flex: 1; height: 1.5px; margin: 0 8px; border-radius: 2px; transition: background .3s; }
 
  /* ── Step card ── */
  .dsk-card {
    background: ${T.surface}; border: 1px solid ${T.border};
    border-radius: 18px; margin-bottom: 16px; overflow: hidden;
    box-shadow: ${T.shadowCard};
  }
  .dsk-card-header {
    padding: 18px 24px; display: flex; align-items: center; gap: 14px;
    border-bottom: 1px solid ${T.border};
    background: linear-gradient(135deg, #fafcf9 0%, #f5f8f4 100%);
  }
  .dsk-card-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: ${T.greenLight}; display: flex; align-items: center;
    justify-content: center; font-size: 20px; flex-shrink: 0;
    border: 1px solid ${T.greenGlow};
  }
  .dsk-card-title {
    font-size: 15px; font-weight: 600; color: ${T.text};
    font-family: 'DM Serif Display', serif;
  }
  .dsk-card-sub { font-size: 12px; color: ${T.textMuted}; margin-top: 2px; }
  .dsk-card-body { padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; }
 
  /* ── Fields ── */
  .dsk-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .dsk-field { display: flex; flex-direction: column; gap: 6px; }
  .dsk-label {
    font-size: 12.5px; font-weight: 500; color: ${T.textMid};
    display: flex; align-items: center; gap: 4px; letter-spacing: 0.2px;
  }
  .dsk-req { color: ${T.red}; font-size: 11px; line-height: 1; }
  .dsk-input-wrap { position: relative; }
  .dsk-input {
    width: 100%; height: 42px; padding: 0 14px;
    font-size: 13.5px; font-family: 'DM Sans', sans-serif;
    border: 1.5px solid ${T.border}; border-radius: 10px;
    background: #fafcf9; color: ${T.text};
    outline: none; transition: border-color .2s, box-shadow .2s, background .2s;
    appearance: none; -webkit-appearance: none;
  }
  .dsk-input:hover { border-color: #b8cdb0; background: #fff; }
  .dsk-input:focus { border-color: ${T.borderFocus}; background: #fff; box-shadow: 0 0 0 3px ${T.greenGlow}; }
  .dsk-input.error { border-color: ${T.red}; background: ${T.redSoft}; }
  .dsk-input.error:focus { box-shadow: 0 0 0 3px rgba(192,57,43,0.12); }
  textarea.dsk-input { height: auto; padding: 12px 14px; resize: vertical; line-height: 1.5; }
  .dsk-err {
    font-size: 11.5px; color: ${T.red}; display: flex; align-items: center; gap: 4px; margin-top: 1px;
  }
 
  /* ── Info banner ── */
  .dsk-info-banner {
    display: flex; align-items: center; gap: 8px;
    background: ${T.accentSoft}; border: 1px solid #f5daa0;
    border-radius: 10px; padding: 10px 14px; font-size: 12.5px;
    color: #8a5c0e; font-weight: 500;
  }
 
  /* ── Buttons ── */
  .dsk-actions { display: flex; gap: 12px; margin-top: 4px; }
  .dsk-btn-back {
    flex: 0 0 auto; padding: 0 22px; height: 46px;
    background: ${T.surface}; border: 1.5px solid ${T.border};
    border-radius: 12px; font-size: 13.5px; font-weight: 500;
    color: ${T.textMid}; cursor: pointer; font-family: 'DM Sans', sans-serif;
    transition: border-color .2s, color .2s, background .2s;
    display: flex; align-items: center; gap: 6px;
  }
  .dsk-btn-back:hover { border-color: ${T.green}; color: ${T.green}; background: ${T.greenLight}; }
  .dsk-btn-next {
    flex: 1; height: 46px;
    background: linear-gradient(135deg, ${T.greenMid} 0%, ${T.green} 100%);
    border: none; border-radius: 12px;
    font-size: 14px; font-weight: 600; color: white;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    transition: transform .15s, box-shadow .2s, opacity .2s;
    box-shadow: 0 4px 14px rgba(30,107,58,0.3);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    letter-spacing: 0.2px;
  }
  .dsk-btn-next:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(30,107,58,0.38);
  }
  .dsk-btn-next:active:not(:disabled) { transform: translateY(0); }
  .dsk-btn-next:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
 
  /* ── Success screen ── */
  .dsk-success-wrap {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: ${T.bg}; padding: 24px;
    background-image: radial-gradient(ellipse 80% 40% at 50% -10%, rgba(30,107,58,0.09) 0%, transparent 70%);
  }
  .dsk-success-card {
    background: ${T.surface}; border: 1px solid ${T.border};
    border-radius: 22px; padding: 52px 40px; text-align: center;
    max-width: 420px; width: 100%;
    box-shadow: ${T.shadowCard};
  }
  .dsk-success-icon {
    width: 72px; height: 72px; border-radius: 50%;
    background: ${T.greenLight}; border: 2px solid ${T.greenGlow};
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 24px; font-size: 32px;
    box-shadow: 0 0 0 8px rgba(30,107,58,0.06);
  }
  .dsk-success-title {
    font-family: 'DM Serif Display', serif; font-size: 24px;
    color: ${T.text}; margin-bottom: 10px;
  }
  .dsk-success-body {
    font-size: 14px; color: ${T.textMuted}; line-height: 1.6; max-width: 320px; margin: 0 auto;
  }
  .dsk-ref-pill {
    display: inline-flex; align-items: center; gap: 6px;
    background: ${T.greenLight}; color: ${T.green};
    border: 1px solid ${T.greenGlow}; border-radius: 24px;
    font-size: 13px; font-weight: 600; padding: 6px 18px; margin-top: 20px;
    letter-spacing: 0.5px;
  }
  .dsk-contact-note {
    margin-top: 24px; padding: 14px 18px;
    background: ${T.accentSoft}; border: 1px solid #f5daa0; border-radius: 12px;
    font-size: 12.5px; color: #8a5c0e;
  }
 
  @media (max-width: 540px) {
    .dsk-row2 { grid-template-columns: 1fr; }
    .dsk-card-body { padding: 16px; }
    .dsk-card-header { padding: 14px 16px; }
    .dsk-stepper { padding: 10px 14px; }
    .dsk-step-desc { display: none; }
  }
`;
 
function StyleInjector() {
  return <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />;
}
 
/* ─── Main component ─────────────────────────────────────────── */
export default function DSKFullForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // const [refNum] = useState("DSK-" + Math.floor(100000 + Math.random() * 900000));
 
  const fetchStates = async () => {
    try {
      setLoading(true);
 
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/states",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            country: "India",
          }),
        },
      );
 
      const result = await response.json();
      setStates(result?.data?.states || []);
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    fetchStates();
  }, []);
 
  const handleStateChange = async (stateName) => {
    setForm((prev) => ({
      ...prev,
      state: stateName,
      district: "",
    }));
 
    setDistricts([]);
 
    try {
      setLoading(true);
 
      const response = await fetch(
        "https://countriesnow.space/api/v0.1/countries/state/cities",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            country: "India",
            state: stateName,
          }),
        },
      );
 
      const result = await response.json();
 
      setDistricts(result?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: "" }));
  };
 
  const validateStep = (s) => {
    const errs = {};
    STEP_FIELDS[s].forEach((f) => {
      if (!form[f]?.trim()) errs[f] = "This field is required";
    });
    if (s === 1) {
      if (form.phoneNumber && !/^[0-9]{10}$/.test(form.phoneNumber))
        errs.phoneNumber = "Enter a valid 10-digit number";
      if (form.aadhaarNumber && !/^[0-9]{12}$/.test(form.aadhaarNumber))
        errs.aadhaarNumber = "Must be exactly 12 digits";
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        errs.email = "Enter a valid email address";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
 
  const goNext = () => {
    if (validateStep(step)) setStep((s) => s + 1);
  };
  const goBack = () => setStep((s) => s - 1);
 
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    try {
      setLoading(true);
      await axios.post("http://localhost:6002/submit-dsk-form", form);
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
 
  if (submitted)
    return (
      <>
        <StyleInjector />
        <SuccessScreen />
      </>
    );
 
  return (
    <>
      <StyleInjector />
      <div className="dsk-page">
        <div className="dsk-shell">
          {/* Header */}
          <header className="dsk-header">
            <div className="dsk-brand">
              <div className="dsk-logo">🌱</div>
              <div>
                <div className="dsk-brand-name">DSK Portal</div>
                <div className="dsk-brand-sub">DRONE SUVIDHA KENDRA</div>
              </div>
            </div>
            <div className="dsk-badge">📋 Application Form</div>
          </header>
 
          {/* Progress bar */}
          <div className="dsk-progress-track">
            <div
              className="dsk-progress-fill"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
 
          {/* Stepper */}
          <div className="dsk-stepper">
            {STEPS.map((st, i) => {
              const state =
                st.id < step ? "done" : st.id === step ? "active" : "idle";
              return (
                <React.Fragment key={st.id}>
                  <div className="dsk-step">
                    <div className={`dsk-step-circle ${state}`}>
                      {st.id < step ? "✓" : st.id}
                    </div>
                    <div className="dsk-step-text">
                      <span className={`dsk-step-label ${state}`}>
                        {st.label}
                      </span>
                      <span className="dsk-step-desc">{st.desc}</span>
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className="dsk-step-divider"
                      style={{
                        background: step > st.id ? T.greenMid : T.border,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
 
          {/* Step 1 */}
          {step === 1 && (
            <div className="dsk-card">
              <div className="dsk-card-header">
                <div className="dsk-card-icon">🙋</div>
                <div>
                  <div className="dsk-card-title">Personal Information</div>
                  <div className="dsk-card-sub">
                    Your basic details for identification and communication
                  </div>
                </div>
              </div>
              <div className="dsk-card-body">
                <div className="dsk-row2">
                  <Field
                    label="Full Name"
                    name="fullName"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                  <Field
                    label="Phone Number"
                    name="phoneNumber"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    placeholder="10-digit mobile number"
                  />
                </div>
                <div className="dsk-row2">
                  <Field
                    label="Email Address"
                    name="email"
                    type="email"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                  <Field
                    label="Aadhaar Number"
                    name="aadhaarNumber"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    placeholder="12-digit Aadhaar"
                    maxLength={12}
                  />
                </div>
                <div className="dsk-row2">
                  <SelectField
                    label="Educational Qualification"
                    name="educationalQualification"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={[
                      "Below 10th",
                      "10th Pass",
                      "12th",
                      "Graduate",
                      "Other",
                    ]}
                  />
                  <SelectField
                    label="Age Group"
                    name="ageGroup"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={["Below 21", "21-35", "36-45", "Above 45"]}
                  />
                </div>
              </div>
            </div>
          )}
 
          {/* Step 2 */}
          {step === 2 && (
            <div className="dsk-card">
              <div className="dsk-card-header">
                <div className="dsk-card-icon">📍</div>
                <div>
                  <div className="dsk-card-title">Location Details</div>
                  <div className="dsk-card-sub">
                    Your current residential address information
                  </div>
                </div>
              </div>
              <div className="dsk-card-body">
                <div className="dsk-row2">
                  <SelectField
                    label="State"
                    name="state"
                    form={form}
                    errors={errors}
                    required
                    onChange={(e) => handleStateChange(e.target.value)}
                    options={states.map((state) => state.name)}
                  />
                  <SelectField
                    label="District"
                    name="district"
                    form={form}
                    errors={errors}
                    required
                    onChange={handleChange}
                    options={districts}
                  />
                </div>
                <div className="dsk-row2">
                  <Field
                    label="Tehsil"
                    name="tehsil"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    placeholder="Tehsil"
                  />
                  <Field
                    label="Block"
                    name="block"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    placeholder="Block"
                  />
                </div>
                <div className="dsk-field">
                  <label className="dsk-label">
                    Full Address <span className="dsk-req">*</span>
                  </label>
                  <textarea
                    className={`dsk-input${errors.address ? " error" : ""}`}
                    name="address"
                    rows={3}
                    value={form.address}
                    onChange={handleChange}
                    placeholder="House / flat, street, village, PIN code"
                  />
                  {errors.address && (
                    <span className="dsk-err">⚠ {errors.address}</span>
                  )}
                </div>
              </div>
            </div>
          )}
 
          {/* Step 3 */}
          {step === 3 && (
            <div className="dsk-card">
              <div className="dsk-card-header">
                <div className="dsk-card-icon">📋</div>
                <div>
                  <div className="dsk-card-title">
                    Eligibility & Financial Readiness
                  </div>
                  <div className="dsk-card-sub">
                    Help us understand your current situation and availability
                  </div>
                </div>
              </div>
              <div className="dsk-card-body">
                <div className="dsk-info-banner">
                  ℹ️ All fields in this section are mandatory to proceed
                </div>
                <div className="dsk-row2">
                  <SelectField
                    label="Available for 8-day training?"
                    name="trainingAvailability"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={["Yes", "No"]}
                  />
                  <SelectField
                    label="Documents ready?"
                    name="documentStatus"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={["Yes", "Working on it", "No"]}
                  />
                </div>
                <div className="dsk-row2">
                  <SelectField
                    label="Looking for loan option?"
                    name="loanOption"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={["Yes", "No, I have own funds"]}
                  />
                  <SelectField
                    label="Ready to proceed?"
                    name="readyToProceed"
                    form={form}
                    errors={errors}
                    onChange={handleChange}
                    required
                    options={["Yes immediately", "Need 1-2 weeks", "Not sure"]}
                  />
                </div>
                <SelectField
                  label="Can you pay 10% booking amount upfront?"
                  name="bookingAmountStatus"
                  form={form}
                  errors={errors}
                  onChange={handleChange}
                  required
                  options={[
                    "YES — I can pay right now",
                    "YES — I can pay within 7 days",
                    "I need to apply for a loan first",
                    "Not sure yet",
                  ]}
                />
              </div>
            </div>
          )}
 
          {/* Actions */}
          <div className="dsk-actions">
            {step > 1 && (
              <button className="dsk-btn-back" onClick={goBack}>
                ← Back
              </button>
            )}
            {step < 3 ? (
              <button className="dsk-btn-next" onClick={goNext}>
                Continue <span style={{ fontSize: 16 }}>→</span>
              </button>
            ) : (
              <button
                className="dsk-btn-next"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  "Submitting…"
                ) : (
                  <>
                    Submit Application <span style={{ fontSize: 16 }}>↗</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
 
/* ─── Field components ───────────────────────────────────────── */
function Field({
  label,
  name,
  type = "text",
  form,
  errors,
  onChange,
  required,
  placeholder,
  maxLength,
}) {
  return (
    <div className="dsk-field">
      <label className="dsk-label">
        {label} {required && <span className="dsk-req">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`dsk-input${errors[name] ? " error" : ""}`}
      />
      {errors[name] && <span className="dsk-err">⚠ {errors[name]}</span>}
    </div>
  );
}
 
function SelectField({
  label,
  name,
  form,
  errors,
  onChange,
  required,
  options,
}) {
  return (
    <div className="dsk-field">
      <label className="dsk-label">
        {label} {required && <span className="dsk-req">*</span>}
      </label>
      <div className="dsk-input-wrap" style={{ position: "relative" }}>
        <select
          name={name}
          value={form[name]}
          onChange={onChange}
          className={`dsk-input${errors[name] ? " error" : ""}`}
          style={{
            paddingRight: 36,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a8c76' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 12px center",
          }}
        >
          <option value="">Select an option</option>
          {(options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
      {errors[name] && <span className="dsk-err">⚠ {errors[name]}</span>}
    </div>
  );
}
 
/* ─── Success screen ─────────────────────────────────────────── */
function SuccessScreen({ refNum }) {
  return (
    <div className="dsk-success-wrap">
      <div className="dsk-success-card">
        <div className="dsk-success-icon">✅</div>
        <h2 className="dsk-success-title">Application Submitted!</h2>
        <p className="dsk-success-body">
          Thank you for applying to the Drone Suvidha Kendra programme. Our team
          will carefully review your details and get in touch with you shortly.
        </p>
        {/* <div className="dsk-ref-pill">
          🔖 Reference: {refNum}
        </div> */}
        <div className="dsk-contact-note">
          ⏱ Expected response within <strong>2–3 working days</strong>. Keep
          your phone accessible.
        </div>
      </div>
    </div>
  );
}